import { ref, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useFeatureFlags } from '@/modules/common/public'

export interface TutorialStep {
  id: string
  title: string
  content: string
  target?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  action?: 'click' | 'hover' | 'input' | 'wait'
  actionTarget?: string
  nextRoute?: string
  skippable?: boolean
  highlight?: boolean
  delay?: number
}

export interface Tutorial {
  id: string
  name: string
  description: string
  category: 'onboarding' | 'feature' | 'advanced'
  steps: TutorialStep[]
  prerequisites?: string[]
  estimatedTime?: number
}

const tutorials: Tutorial[] = [
  {
    id: 'first-login',
    name: 'Первый вход в систему',
    description: 'Знакомство с основными возможностями ЛабАктив',
    category: 'onboarding',
    estimatedTime: 5,
    steps: [
      {
        id: 'welcome',
        title: 'Добро пожаловать в ЛабАктив!',
        content:
          'ЛабАктив - это современная B2B платформа для торговли лабораторным оборудованием. Давайте познакомимся с основными возможностями системы.',
        skippable: true,
      },
      {
        id: 'navigation',
        title: 'Навигация по системе',
        content:
          'В верхней части экрана расположено главное меню. Здесь вы найдете все основные разделы системы.',
        target: '.app-bar',
        position: 'bottom',
        highlight: true,
      },
      {
        id: 'catalog',
        title: 'Каталог товаров',
        content:
          'В каталоге представлено более 1000 наименований лабораторного оборудования от ведущих производителей.',
        target: '[data-tutorial="catalog-link"]',
        position: 'bottom',
        action: 'click',
        nextRoute: '/catalog',
      },
      {
        id: 'search',
        title: 'Поиск товаров',
        content:
          'Используйте поиск для быстрого нахождения нужного оборудования. Поиск работает по названию, артикулу и характеристикам.',
        target: '[data-tutorial="search-input"]',
        position: 'bottom',
        action: 'input',
      },
      {
        id: 'filters',
        title: 'Фильтры',
        content:
          'Фильтры помогут найти товары по категории, производителю, цене и другим параметрам.',
        target: '[data-tutorial="filters"]',
        position: 'right',
      },
      {
        id: 'product-card',
        title: 'Карточка товара',
        content:
          'В карточке товара вы найдете подробную информацию, характеристики, отзывы и возможность добавить в корзину.',
        target: '.product-card:first-child',
        position: 'top',
        action: 'click',
      },
      {
        id: 'favorites',
        title: 'Избранное',
        content:
          'Добавляйте интересные товары в избранное для быстрого доступа. Для этого нажмите на иконку сердечка.',
        target: '[data-tutorial="favorite-btn"]',
        position: 'top',
        action: 'click',
      },
      {
        id: 'cart',
        title: 'Корзина',
        content:
          'В корзине собираются все товары для заказа. Здесь можно изменить количество и оформить заказ.',
        target: '[data-tutorial="cart-btn"]',
        position: 'bottom',
      },
      {
        id: 'profile',
        title: 'Профиль пользователя',
        content:
          'В профиле вы можете управлять своими данными, просматривать историю заказов и настраивать уведомления.',
        target: '[data-tutorial="profile-menu"]',
        position: 'bottom',
      },
      {
        id: 'complete',
        title: 'Готово!',
        content:
          'Теперь вы знаете основы работы с ЛабАктив. Приятных покупок! Если возникнут вопросы, обращайтесь в службу поддержки.',
        skippable: false,
      },
    ],
  },
  {
    id: 'procurement-process',
    name: 'Создание закупки',
    description: 'Пошаговое руководство по созданию и проведению закупки',
    category: 'feature',
    estimatedTime: 10,
    prerequisites: ['first-login'],
    steps: [
      {
        id: 'start-procurement',
        title: 'Создание новой закупки',
        content:
          'Закупка - это упрощенная процедура для быстрого приобретения товаров. Начнем создание новой закупки.',
        target: '[data-tutorial="create-procurement"]',
        position: 'bottom',
        action: 'click',
        nextRoute: '/procurements/create',
      },
      {
        id: 'procurement-details',
        title: 'Основная информация',
        content: 'Заполните название закупки, описание и установите сроки подачи предложений.',
        target: '[data-tutorial="procurement-form"]',
        position: 'right',
      },
      {
        id: 'add-positions',
        title: 'Добавление позиций',
        content:
          'Добавьте товары или услуги, которые хотите закупить. Укажите количество и требования.',
        target: '[data-tutorial="add-position"]',
        position: 'top',
        action: 'click',
      },
      {
        id: 'evaluation-criteria',
        title: 'Критерии оценки',
        content: 'Установите критерии для оценки предложений: цена, качество, сроки поставки.',
        target: '[data-tutorial="evaluation-criteria"]',
        position: 'left',
      },
      {
        id: 'publish-procurement',
        title: 'Публикация закупки',
        content:
          'После заполнения всех данных опубликуйте закупку. Поставщики получат уведомления.',
        target: '[data-tutorial="publish-btn"]',
        position: 'top',
        action: 'click',
      },
      {
        id: 'monitor-offers',
        title: 'Мониторинг предложений',
        content: 'Отслеживайте поступающие предложения в разделе "Мои закупки".',
        target: '[data-tutorial="my-procurements"]',
        position: 'bottom',
      },
    ],
  },
  {
    id: 'tender-participation',
    name: 'Участие в тендере',
    description: 'Как подать заявку на участие в тендере',
    category: 'feature',
    estimatedTime: 8,
    steps: [
      {
        id: 'find-tender',
        title: 'Поиск тендеров',
        content: 'В разделе "Тендеры" вы найдете все доступные тендерные процедуры.',
        target: '[data-tutorial="tenders-link"]',
        position: 'bottom',
        nextRoute: '/tenders',
      },
      {
        id: 'tender-details',
        title: 'Изучение условий',
        content: 'Внимательно изучите техническое задание, требования и критерии оценки.',
        target: '[data-tutorial="tender-details"]',
        position: 'right',
      },
      {
        id: 'submit-application',
        title: 'Подача заявки',
        content:
          'Заполните техническое и коммерческое предложение, приложите необходимые документы.',
        target: '[data-tutorial="submit-application"]',
        position: 'top',
        action: 'click',
      },
      {
        id: 'track-status',
        title: 'Отслеживание статуса',
        content: 'Следите за статусом своей заявки в личном кабинете.',
        target: '[data-tutorial="application-status"]',
        position: 'left',
      },
    ],
  },
]

// Shared tutorial state across the whole app (singleton composable state).
const currentTutorial = ref<Tutorial | null>(null)
const currentStepIndex = ref(0)
const isActive = ref(false)
const isVisible = ref(false)
const completedTutorials = ref<string[]>([])
let isInitialized = false
let highlightedSelector: string | null = null

export function useTutorial() {
  const router = useRouter()
  const feature_flags = useFeatureFlags()

  // Загрузка завершенных туториалов из localStorage
  const loadCompletedTutorials = () => {
    const saved = localStorage.getItem('labactive-completed-tutorials')
    if (saved) {
      completedTutorials.value = JSON.parse(saved)
    }
  }

  // Сохранение завершенных туториалов
  const saveCompletedTutorials = () => {
    localStorage.setItem('labactive-completed-tutorials', JSON.stringify(completedTutorials.value))
  }

  const currentStep = computed(() => {
    if (!currentTutorial.value) return null
    return currentTutorial.value.steps[currentStepIndex.value] || null
  })

  const isFirstStep = computed(() => currentStepIndex.value === 0)
  const isLastStep = computed(() => {
    if (!currentTutorial.value) return false
    return currentStepIndex.value === currentTutorial.value.steps.length - 1
  })

  const progress = computed(() => {
    if (!currentTutorial.value) return 0
    return Math.round(((currentStepIndex.value + 1) / currentTutorial.value.steps.length) * 100)
  })

  const availableTutorials = computed(() => {
    return tutorials.filter((tutorial) => {
      if (
        tutorial.id === 'tender-participation' &&
        !feature_flags.isEnabled('tender_extended_enabled')
      ) {
        return false
      }
      if (
        tutorial.id === 'procurement-process' &&
        !feature_flags.isEnabled('procurement_enabled')
      ) {
        return false
      }
      // Проверяем предварительные условия
      if (tutorial.prerequisites) {
        return tutorial.prerequisites.every((prereq) => completedTutorials.value.includes(prereq))
      }
      return true
    })
  })

  const startTutorial = async (tutorialId: string) => {
    const tutorial = tutorials.find((t) => t.id === tutorialId)
    if (!tutorial) return

    currentTutorial.value = tutorial
    currentStepIndex.value = 0
    isActive.value = true

    await nextTick()
    showStep()
  }

  const showStep = async () => {
    if (!currentStep.value) return

    // Переход на нужную страницу если указан маршрут
    if (
      currentStep.value.nextRoute &&
      router.currentRoute.value.path !== currentStep.value.nextRoute
    ) {
      if (
        currentStep.value.nextRoute.startsWith('/tenders') &&
        !feature_flags.isEnabled('tender_extended_enabled')
      ) {
        completeTutorial()
        return
      }
      if (
        (currentStep.value.nextRoute.startsWith('/procurements') ||
          currentStep.value.nextRoute.startsWith('/procurement')) &&
        !feature_flags.isEnabled('procurement_enabled')
      ) {
        completeTutorial()
        return
      }
      await router.push(currentStep.value.nextRoute)
      await nextTick()
      // Небольшая задержка для загрузки компонентов
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    // Задержка перед показом шага
    const step_delay = currentStep.value.delay ?? 0
    if (step_delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, step_delay))
    }

    isVisible.value = true

    // Подсветка элемента
    if (currentStep.value.target && currentStep.value.highlight) {
      highlightElement(currentStep.value.target)
    }

    // Прокрутка к элементу
    if (currentStep.value.target) {
      scrollToElement(currentStep.value.target)
    }
  }

  const nextStep = async () => {
    if (!currentTutorial.value) return

    // Выполнение действия если указано
    if (currentStep.value?.action && currentStep.value?.actionTarget) {
      await performAction(currentStep.value.action, currentStep.value.actionTarget)
    }

    isVisible.value = false
    removeHighlight()

    if (isLastStep.value) {
      completeTutorial()
    } else {
      currentStepIndex.value++
      await nextTick()
      setTimeout(showStep, 300)
    }
  }

  const prevStep = () => {
    if (isFirstStep.value) return

    isVisible.value = false
    removeHighlight()
    currentStepIndex.value--

    setTimeout(showStep, 300)
  }

  const skipTutorial = () => {
    isVisible.value = false
    isActive.value = false
    removeHighlight()
    currentTutorial.value = null
  }

  const completeTutorial = () => {
    if (!currentTutorial.value) return

    // Добавляем в завершенные
    if (!completedTutorials.value.includes(currentTutorial.value.id)) {
      completedTutorials.value.push(currentTutorial.value.id)
      saveCompletedTutorials()
    }

    isVisible.value = false
    isActive.value = false
    removeHighlight()
    currentTutorial.value = null

    // Показываем уведомление о завершении
    // Можно добавить интеграцию с системой уведомлений позже
  }

  const highlightElement = (selector: string) => {
    const element = document.querySelector(selector) as HTMLElement | null
    if (element) {
      highlightedSelector = selector
      element.classList.add('tutorial-highlight')
    }
  }

  const removeHighlight = () => {
    if (highlightedSelector) {
      const element = document.querySelector(highlightedSelector)
      if (element) {
        element.classList.remove('tutorial-highlight')
      }
      highlightedSelector = null
      return
    }

    document.querySelectorAll('.tutorial-highlight').forEach((el) => {
      el.classList.remove('tutorial-highlight')
    })
  }

  const scrollToElement = (selector: string) => {
    const element = document.querySelector(selector)
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      })
    }
  }

  const performAction = async (action: string, target: string) => {
    const element = document.querySelector(target) as HTMLElement
    if (!element) return

    switch (action) {
      case 'click':
        element.click()
        break
      case 'hover':
        element.dispatchEvent(new MouseEvent('mouseenter'))
        break
      case 'input':
        if (element instanceof HTMLInputElement) {
          element.focus()
          element.value = 'Пример поиска'
          element.dispatchEvent(new Event('input'))
        }
        break
      case 'wait':
        await new Promise((resolve) => setTimeout(resolve, 1000))
        break
    }
  }

  const getTutorialById = (id: string) => {
    return tutorials.find((t) => t.id === id)
  }

  const isTutorialCompleted = (id: string) => {
    return completedTutorials.value.includes(id)
  }

  const resetTutorial = (id: string) => {
    completedTutorials.value = completedTutorials.value.filter((t) => t !== id)
    saveCompletedTutorials()
  }

  const resetAllTutorials = () => {
    completedTutorials.value = []
    saveCompletedTutorials()
  }

  const startOnboarding = () => {
    // Запускаем первый туториал (онбординг)
    startTutorial('first-login')
  }

  const shouldShowOnboarding = computed(() => {
    // Показываем онбординг если пользователь не прошел базовый туториал
    return !isTutorialCompleted('first-login')
  })

  // Инициализация (только один раз)
  if (!isInitialized) {
    loadCompletedTutorials()
    isInitialized = true
  }

  return {
    // State
    currentTutorial,
    currentStep,
    currentStepIndex,
    isActive,
    isVisible,
    completedTutorials,

    // Computed
    isFirstStep,
    isLastStep,
    progress,
    availableTutorials,
    shouldShowOnboarding,

    // Methods
    startTutorial,
    startOnboarding,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial,
    getTutorialById,
    isTutorialCompleted,
    resetTutorial,
    resetAllTutorials,
  }
}

// CSS стили для анимации
const tutorialStyles = `
@keyframes tutorial-pulse {
  0% { box-shadow: 0 0 0 0 rgba(20, 51, 94, 0.35); }
  70% { box-shadow: 0 0 0 10px rgba(20, 51, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(20, 51, 94, 0); }
}

.tutorial-highlight {
  position: relative;
  z-index: 10000 !important;
  outline: 2px solid var(--color-accent) !important;
  border-radius: 8px !important;
  animation: tutorial-pulse 2s infinite;
}
`

// Добавляем стили в head
if (typeof document !== 'undefined') {
  const style = document.createElement('style')
  style.textContent = tutorialStyles
  document.head.appendChild(style)
}
